import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import AttachmentViewer from 'components/forms/fieldsets/AttachmentViewer';
import '../../style/scss/card.scss';

interface ProjectPhotoType {
    id: number;
    file: string;
    thumbnail: string | null;
    filename: string;
    description: string | null;
    created: string;
    added_at: string | null;
}

interface ProjectPhotoGroupType {
    date: string;
    job_id: number | null;
    job_number: string | null;
    photos: ProjectPhotoType[];
}

const ProjectPhotosTab: React.FC<{ itemId: string }> = ({ itemId }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { userLocale } = useSettings();

    // Fetch the photos of the jobs of the project
    const { response, loading } = useFetchData({
        apiUrl: `get_project_photo_list/${itemId}`
    });

    // Get the photo groups from the response
    const photoGroups: ProjectPhotoGroupType[] = useMemo(() => {
        return response?.results || [];
    }, [response]);

    // Format the date with written out day name and month
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(userLocale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Handle viewing of a photo
    const handleViewPhoto = (photo: ProjectPhotoType) => {
        initializeModal(
            <AttachmentViewer file={photo.file} filename={photo.filename} type='photo' />,
            { modalSize: 'medium-large', modalSpacing: 'compact' }
        );
    };

    // Show a loader while the photos are fetched
    if (loading === 'loading') {
        return <div className="loader" />;
    }

    // Show a message when the project has no photos
    if (!photoGroups.length) {
        return (
            <div className="card project-photos-list empty">
                <p>{t('project.general.no_photos')}</p>
                <p className='photos-explanation'>{t('project.general.photos_explanationText')}</p>
            </div>
        );
    }

    return (
        <div className='project-photos'>
            <p className='photos-explanation'>{t('project.general.photos_explanationText')}</p>
            {photoGroups.map((group) => (
                <div key={`${group.date}-${group.job_id}`} className='card photoscard'>
                    <div className='photos-header'>
                        <h6>
                            {formatDate(group.date)}
                            {group.job_number && (
                                <span className='photos-job-number'>
                                    {t('timerow.general.job_label')} {group.job_number}
                                </span>
                            )}
                        </h6>
                    </div>
                    <div className='photos-list'>
                        {group.photos.map((photo) => (
                            <div key={photo.id}
                                 onClick={() => handleViewPhoto(photo)}
                                 className='photo-box tooltip-icon'>
                                <img src={photo.thumbnail || photo.file}
                                     alt={photo.filename}
                                     className='photo' />
                                <span className="tooltip tooltip-bottom">
                                    {photo.description ? (
                                        <div>{photo.description}</div>
                                    ) : (
                                        <div>{photo.filename}</div>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ProjectPhotosTab;
